import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchDeviceConfigFields,
  fetchLayoutConfig,
  fetchDeviceStatus,
  fetchDeviceSettings,
  fetchDeviceActions,
  fetchDevicesWithAlarms,
  fetchHiddenCircuits,
  fetchSiblingsInfo
} from 'src/module/device/action';
import { deviceTypeConfig } from 'src/utils/deviceTypeConfig';

/**
 * Custom hook that loads device data efficiently
 * Uses useEffect (standard practice for data fetching) to start fetches
 * as soon as the component mounts, with proper dependency tracking.
 */
export function useDeviceDataLoader (
  device: any,
  deviceId: string | undefined,
  deviceTagFromUrl: string | null,
  childTagFromUrl: string,
  urlDeviceTag: string,
) {
  const dispatch = useDispatch();
  const lastDeviceId = useRef<string | undefined>(undefined);
  const lastDeviceTag = useRef<string | null>(null);
  const lastChildTag = useRef<string>('');

  const deviceType = device?.get('type');
  const deviceFactory = device?.get('device_factory', null);
  const isOffline = device?.get('offline');
  const parentIsController = device?.get('parent_type') === 'controller';
  const parentId = device?.get('parent_id');

  // Use useEffect for data fetching (standard practice)
  // This runs after paint but starts async fetches immediately
  useEffect(() => {
    // Check if we need to fetch (deviceId or tags changed)
    const shouldFetch =
      deviceId !== lastDeviceId.current ||
      deviceTagFromUrl !== lastDeviceTag.current ||
      childTagFromUrl !== lastChildTag.current;

    if (shouldFetch) {
      // Fetch device with alarms (this loads the device itself)
      if (childTagFromUrl !== 'undefined' && deviceTagFromUrl) {
        dispatch(fetchDevicesWithAlarms(deviceTagFromUrl, childTagFromUrl));
      } else {
        dispatch(fetchDevicesWithAlarms(urlDeviceTag));
      }
      if (deviceId) {
        // Fetch device status if supported and not offline
        if (deviceTypeConfig[deviceType]?.fetchDeviceStatus && !isOffline) {
          dispatch(fetchDeviceStatus(deviceId));
        }

        // Fetch device settings if not offline
        if (!isOffline) {
          dispatch(fetchDeviceSettings(deviceId));
        }

        // Always fetch hidden circuits
        dispatch(fetchHiddenCircuits(deviceId));
      }

      // Update refs to track what we've fetched
      lastDeviceId.current = deviceId;
      lastDeviceTag.current = deviceTagFromUrl;
      lastChildTag.current = childTagFromUrl;
    }
  }, [deviceId, deviceTagFromUrl, childTagFromUrl, urlDeviceTag, deviceType, isOffline, dispatch]);

  // Fetch config fields and actions when deviceFactory is available
  useEffect(() => {
    if (deviceId && deviceFactory && deviceTypeConfig[deviceType]?.fetchDeviceConfigFields) {
      dispatch(fetchDeviceConfigFields(deviceId));
    }
    if (deviceId && deviceFactory) {
      dispatch(fetchLayoutConfig(deviceId));
    }
    if (deviceId && deviceFactory && deviceTypeConfig[deviceType]?.fetchDeviceActions) {
      dispatch(fetchDeviceActions(deviceId));
    }
  }, [deviceFactory, deviceId, deviceType, dispatch]);

  // Fetch siblings info for circuits under controllers
  useEffect(() => {
    if (deviceType === 'circuit' && parentId && parentIsController) {
      dispatch(fetchSiblingsInfo(parentId));
    }
  }, [deviceType, parentId, parentIsController, dispatch]);
}
